import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject, delay, distinctUntilChanged, map, Observable, Subject } from 'rxjs';
import { Order, ProfileState, Sort } from 'shared';
import { PaginateQuery, PaginatorModel } from 'symbols';
import {
  AsteriskEndpoint,
  AsteriskServerSettingsModel,
  CallLogModel,
  ContactModel,
  GP_ASTERISK_CONFIG_TOKEN,
  GpAsteriskConfigModel,
  GpCallModel,
  MonitorResultModel,
  SipAccountModel,
  SoftphoneConfigModel,
  SpyMode,
  ToCallResponseModel,
  TransferTargetModel,
  InternalCallModel,
  MonitorMode
} from '../symbols';

@Injectable({ providedIn: 'root' })
export class GpAsteriskService {
  public sortDefault = { sort: Sort.CREATED, order: Order.DESC };
  public audio = new Audio('/shared/src/assets/sounds/calling.mp3');
  private isPlayRing = false;
  private readonly api = '/api/asterisk';
  private readonly _hasActiveDirectCalls$ = new BehaviorSubject<boolean>(false);
  readonly hasActiveDirectCalls$ = this._hasActiveDirectCalls$.asObservable();

  private readonly _peerStatus$ = new Subject<{ username: string; isRegistered: boolean }>();
  readonly peerStatus$ = this._peerStatus$.asObservable();
  private readonly registeredPeers = new Map<string, boolean>();
  private readonly _peerStatusMap$ = new BehaviorSubject<Map<string, boolean>>(new Map());

  constructor(
    @Optional() @Inject(GP_ASTERISK_CONFIG_TOKEN) private config: GpAsteriskConfigModel,
    private http: HttpClient,
    private store: Store
  ) {
    this.audio.onended = () => {
      setTimeout(this.playWithPause, 1000);
    };

    this.playWithPause();
  }

  playWithPause = () => {
    this.audio.currentTime = 0;
    if (this.isPlayRing) {
      this.audio.play().catch(console.error);
    }
  };

  playRing(volume = 0.5) {
    this.audio.volume = volume;
    // вже дзвонить — не перезапускаємо мелодію з початку на кожне оновлення стану
    if (this.isPlayRing) return;
    this.isPlayRing = true;
    this.playWithPause();
  }

  stopRing() {
    this.isPlayRing = false;
    // зупиняємо одразу, а не після поточного програшу
    this.audio.pause();
    this.audio.currentTime = 0;
  }


  public toCall(tel: string, endpoint?: string): Observable<ToCallResponseModel> {

    const currentUser = this.store.selectSnapshot(ProfileState.currentUser);
    return this.http.post<ToCallResponseModel>(`${this.api}/to-call`, {
      endpoint: endpoint || `SIP/${currentUser.code}`,
      context: 'extension',
      callerId: tel,
      extension: tel,
      app: this.config.appName,
      variables: { 'CALLERID(name)': currentUser.code },
      appArgs: 'out'
    }).pipe(delay(3000));
  }


  public answerOnCall(channel: GpCallModel): Observable<void> {
    const currentUser = this.store.selectSnapshot(ProfileState.currentUser);
    return this.http.post<void>(`${this.api}/answer`, {
      channelId: channel.id,
      nameCaller: channel.nameCaller,
      SIP_number: currentUser.code
    });
  }


  // Консультативне переведення: клієнт на утриманні, вам дзвонить номер SIP_number
  public transferCall(channel: GpCallModel, SIP_number: string, toName?: string): Observable<void> {
    return this.http.post<void>(`${this.api}/transfer`, { channelId: channel.id, SIP_number, toName });
  }

  // Підтвердити — клієнт переходить до номера, з яким ви говорили
  public completeTransfer(channel: GpCallModel): Observable<void> {
    return this.http.post<void>(`${this.api}/transfer/complete`, { channelId: channel.id });
  }

  // Скасувати — ви повертаєтесь до клієнта
  public cancelTransfer(channel: GpCallModel): Observable<void> {
    return this.http.post<void>(`${this.api}/transfer/cancel`, { channelId: channel.id });
  }


  // Asterisk дзвонить на внутрішній номер поточного користувача (бекенд бере його з профілю).
  // Якщо телефон уже на лінії в цій розмові — режим просто перемикається.
  // Оператор запрошує колегу: у конференцію, суфлером (його чує лише оператор) або слухачем.
  // Як консультативна переадресація: клієнт на утриманні, після розмови з колегою — completeTransfer / cancelTransfer
  public inviteToCall(channel: GpCallModel, SIP_number: string, mode: MonitorMode, toName?: string): Observable<void> {
    return this.http.post<void>(`${this.api}/invite`, { channelId: channel.id, SIP_number, mode, toName });
  }

  public spyCall(channel: GpCallModel, mode: SpyMode): Observable<MonitorResultModel> {
    return this.http.post<MonitorResultModel>(`${this.api}/spy`, { channelId: channel.id, mode });
  }

  public joinConference(channel: GpCallModel): Observable<MonitorResultModel> {
    return this.http.post<MonitorResultModel>(`${this.api}/conference`, { channelId: channel.id });
  }

  public getMonitorMode(channel: GpCallModel): Observable<MonitorResultModel | null> {
    return this.http.get<MonitorResultModel | null>(`${this.api}/monitor/${channel.id}`);
  }

  // Той, хто скасовує, лишається з клієнтом сам
  public endConference(channel: GpCallModel): Observable<void> {
    return this.http.post<void>(`${this.api}/conference/end`, { channelId: channel.id });
  }

  public getTransferTargets(): Observable<TransferTargetModel[]> {
    return this.http.get<TransferTargetModel[]>(`${this.api}/transfer-targets`);
  }

  // Внутрішній дзвінок колезі: АТС з'єднується з вами, потім дзвонить йому
  public startInternalCall(SIP_number: string): Observable<InternalCallModel> {
    return this.http.post<InternalCallModel>(`${this.api}/internal-call`, { SIP_number });
  }

  public getEndpoints(): Observable<AsteriskEndpoint[]> {
    return this.http.get<AsteriskEndpoint[]>(`${this.api}/endpoints`);
  }

  public getContacts(isFavorite: boolean | null = null, search?: string): Observable<ContactModel[]> {
    const params: any = {};

    if (search) {
      params.search = search;
    }
    if (isFavorite !== null) {
      params.isFavorite = isFavorite;
    }

    return this.http.get<ContactModel[]>(`${this.api}/contacts/`, { params });
  }


  public getCallLogs(filter?: PaginateQuery<{ search: string }>): Observable<PaginatorModel<CallLogModel>> {
    const params: { [key: string]: any } = {
      page: filter?.page || 1,
      limit: filter?.limit || 50,
      sortBy: `${filter?.sortBy[0][0]}:${filter?.sortBy[0][1]}`,
      search: filter?.filter.search
    };
    return this.http.get<PaginatorModel<CallLogModel>>(`${this.api}/call-logs`, { params });
  }

  public createCallLog(callLog: Partial<CallLogModel>): Observable<CallLogModel> {
    return this.http.post<CallLogModel>(`${this.api}/call-log`, callLog);
  }

  public getCallLogsLazy(url: string): Observable<PaginatorModel<CallLogModel>> {
    return this.http.get<PaginatorModel<CallLogModel>>(url);
  }

  public setFavorite(contactId: string, isFavorite: boolean): Observable<ContactModel> {
    return this.http.patch<ContactModel>(`${this.api}/set-favorite/${contactId}`, { isFavorite });
  }

  public createContact(contact: ContactModel): Observable<ContactModel> {
    return this.http.post<ContactModel>(`${this.api}/contact`, contact);
  }

  public updateContact(contact: ContactModel): Observable<ContactModel> {
    return this.http.patch<ContactModel>(`${this.api}/contact`, contact);
  }

  public deleteContact(id: string): Observable<{ id: string }> {
    return this.http.delete<{ id: string }>(`${this.api}/contact/${id}`);
  }

  public getRoutine(): Observable<any> {
    return this.http.get(`${this.api}/routine`);
  }

  public updateRoutine(data: any): Observable<any> {
    return this.http.put(`${this.api}/routine`, data);
  }

  public getDirectCalls(): Observable<any[]> {
    return this.http.get<any[]>(`${this.api}/direct-calls`);
  }

  public emitPeerStatus(device: string, status: string): void {
    const username = device?.replace(/^SIP\//i, '');
    if (!username) return;
    const isRegistered = status === 'Registered' || status === 'Reachable';
    this.registeredPeers.set(username, isRegistered);
    this._peerStatus$.next({ username, isRegistered });
    this._peerStatusMap$.next(this.registeredPeers);
  }

  public isPeerRegistered(code: string): boolean {
    return this.registeredPeers.get(code) ?? false;
  }

  public peerRegistered$(code: string): Observable<boolean> {
    return this._peerStatusMap$.pipe(
      map(m => m.get(code) ?? false),
      distinctUntilChanged(),
    );
  }

  public refreshDirectCallsStatus(): void {
    this.getDirectCalls().subscribe({
      next: (calls) => this._hasActiveDirectCalls$.next(calls.some(c => c.isEnabled))
    });
  }

  public setDirectCallsStatus(calls: { isEnabled: boolean }[]): void {
    this._hasActiveDirectCalls$.next(calls.some(c => c.isEnabled));
  }

  public createDirectCall(sipNumber: string): Observable<any> {
    return this.http.post(`${this.api}/direct-calls`, { sipNumber });
  }

  public updateDirectCall(id: string, data: { isEnabled?: boolean; sipNumber?: string }): Observable<any> {
    return this.http.patch(`${this.api}/direct-calls/${id}`, data);
  }

  public deleteDirectCall(id: string): Observable<void> {
    return this.http.delete<void>(`${this.api}/direct-calls/${id}`);
  }

  public getServerSettings(): Observable<AsteriskServerSettingsModel> {
    return this.http.get<AsteriskServerSettingsModel>(`${this.api}/server-settings`);
  }

  public updateServerSettings(data: Partial<AsteriskServerSettingsModel>): Observable<AsteriskServerSettingsModel> {
    return this.http.put<AsteriskServerSettingsModel>(`${this.api}/server-settings`, data);
  }

  // увімкнення телефону в застосунку: за потреби бекенд призначає номер і створює SIP-акаунт
  // відповідь із вбудованого телефону: застосунок уже відкрив лінію; SIP-телефон перестає дзвонити
  public answerFromSoftphone(channelId: string): Observable<void> {
    return this.http.post<void>(`${this.api}/softphone/answer`, { channelId });
  }

  // токен пристрою для дзвінків у закритий застосунок: ios — PushKit (VoIP), android — FCM
  public saveSoftphonePushToken(platform: 'ios' | 'android', token: string): Observable<void> {
    return this.http.post<void>(`${this.api}/softphone/push-token`, { platform, token });
  }

  // «Був у мережі»: застосунок відкритий (пристрій упізнається за push-токеном, як і у фонових сигналах)
  public softphoneHeartbeat(token: string): Observable<void> {
    return this.http.post<void>('/api/softphone-presence/heartbeat', { token });
  }

  // телефон отримав вхідний дзвінок — той, хто дзвонить, бачить «Дзвонить у колеги»
  public softphoneDelivered(token: string, channelId: string): Observable<void> {
    return this.http.post<void>('/api/softphone-presence/delivered', { token, channelId });
  }

  public removeSoftphonePushToken(token: string): Observable<void> {
    return this.http.post<void>(`${this.api}/softphone/push-token/remove`, { token });
  }

  public provisionSoftphone(): Observable<SoftphoneConfigModel> {
    return this.http.post<SoftphoneConfigModel>(`${this.api}/softphone/provision`, {});
  }

  // чи ще дзвонить дзвінок, показаний через push (ringing / active / ended)
  public softphoneCallStatus(channelId: string): Observable<{ status: 'ringing' | 'active' | 'ended' }> {
    return this.http.get<{ status: 'ringing' | 'active' | 'ended' }>(`${this.api}/softphone/call-status/${encodeURIComponent(channelId)}`);
  }

  // чи є в користувача застосунок із вбудованим телефоном (дзвінок із вебу піде і через нього)
  public hasSoftphoneApp(): Observable<boolean> {
    return this.http.get<{ hasApp: boolean }>(`${this.api}/softphone/has-app`).pipe(map(res => !!res?.hasApp));
  }

  // логін/пароль власного SIP-номера для вбудованого телефону
  public getSoftphoneConfig(): Observable<SoftphoneConfigModel> {
    return this.http.get<SoftphoneConfigModel>(`${this.api}/softphone`);
  }

  public getSipAccounts(): Observable<SipAccountModel[]> {
    return this.http.get<SipAccountModel[]>(`${this.api}/sip-accounts`);
  }

  public createSipAccount(data: { username: string; password: string; callerid?: string }): Observable<SipAccountModel> {
    return this.http.post<SipAccountModel>(`${this.api}/sip-accounts`, data);
  }

  public updateSipAccount(username: string, data: { password: string; callerid?: string }): Observable<SipAccountModel> {
    return this.http.patch<SipAccountModel>(`${this.api}/sip-accounts/${username}`, data);
  }

  public deleteSipAccount(username: string): Observable<void> {
    return this.http.delete<void>(`${this.api}/sip-accounts/${username}`);
  }
}
